import { AlertTriangle, CalendarRange, TrendingUp, Users, Wallet } from 'lucide-react';
import { NORTHWIND_HEADCOUNT, type HeadcountTeam } from '@/modules/brain/data/seedCompany';
import { companyApi } from '@/modules/brain/services/company.api';
import { useCompanyResource } from '@/modules/brain/hooks/useCompanyResource';

function formatBudget(amount: number) {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K`;
  return `$${amount.toLocaleString()}`;
}

function teamProgress(team: HeadcountTeam) {
  const total = team.plannedHires;
  const filled = team.filled;
  return total === 0 ? 0 : Math.min(1, filled / total);
}

function teamStatus(team: HeadcountTeam): 'on-track' | 'behind' | 'ahead' | 'idle' {
  if (team.plannedHires === 0) return 'idle';
  const elapsed = 0.33; // ~Q1 done + ~1 month into Q2 = ~33% of year
  const progress = teamProgress(team);
  if (progress >= elapsed + 0.15) return 'ahead';
  if (progress <= elapsed - 0.15) return 'behind';
  return 'on-track';
}

const STATUS_LABEL: Record<ReturnType<typeof teamStatus>, string> = {
  'on-track': 'On track',
  behind: 'Behind plan',
  ahead: 'Ahead of plan',
  idle: 'No plan',
};

export function HeadcountPlanPage() {
  const { value: plan } = useCompanyResource(
    () => companyApi.getHeadcount(),
    NORTHWIND_HEADCOUNT,
    'headcount',
  );
  const totalFilled = plan.teams.reduce((acc, t) => acc + t.filled, 0);
  const totalOpen = plan.teams.reduce((acc, t) => acc + t.open, 0);
  const overallProgress = plan.totalPlannedHires === 0 ? 0 : totalFilled / plan.totalPlannedHires;
  const asOf = new Date(plan.asOfDate).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="brain-company-page">
      <header className="brain-company-hero brain-company-hero-compact">
        <div className="brain-company-hero-text">
          <p className="brain-company-eyebrow">Brain · Company</p>
          <h1 className="brain-company-title">FY{plan.fiscalYear} headcount plan</h1>
          <p className="brain-company-tagline">
            {plan.startingHeadcount} → {plan.endingHeadcountTarget} people · {plan.totalPlannedHires} hires planned ·
            budget {formatBudget(plan.annualBudgetUsd)} · as of {asOf}
          </p>
        </div>
      </header>

      <section className="brain-company-headcount-summary">
        <div className="brain-company-summary-tile">
          <div className="brain-company-summary-tile-label">
            <Users size={12} strokeWidth={1.7} /> Filled
          </div>
          <div className="brain-company-summary-tile-value">{totalFilled}</div>
          <div className="brain-company-summary-tile-sub">of {plan.totalPlannedHires} planned</div>
        </div>
        <div className="brain-company-summary-tile">
          <div className="brain-company-summary-tile-label">
            <CalendarRange size={12} strokeWidth={1.7} /> Open roles
          </div>
          <div className="brain-company-summary-tile-value">{totalOpen}</div>
          <div className="brain-company-summary-tile-sub">across {plan.teams.length} teams</div>
        </div>
        <div className="brain-company-summary-tile">
          <div className="brain-company-summary-tile-label">
            <TrendingUp size={12} strokeWidth={1.7} /> Plan progress
          </div>
          <div className="brain-company-summary-tile-value">
            {Math.round(overallProgress * 100)}%
          </div>
          <div className="brain-company-summary-tile-sub">at {Math.round(0.33 * 100)}% of year elapsed</div>
        </div>
        <div className="brain-company-summary-tile">
          <div className="brain-company-summary-tile-label">
            <Wallet size={12} strokeWidth={1.7} /> Budget
          </div>
          <div className="brain-company-summary-tile-value">{formatBudget(plan.annualBudgetUsd)}</div>
          <div className="brain-company-summary-tile-sub">fully-loaded comp</div>
        </div>
      </section>

      <section className="brain-company-section">
        <header className="brain-company-section-header">
          <Users size={14} strokeWidth={1.6} />
          <h2>By team</h2>
          <span className="brain-company-section-hint">Quarterly cadence; open roles in the queue.</span>
        </header>
        <ul className="brain-company-team-list">
          {plan.teams.map((team) => {
            const status = teamStatus(team);
            const progress = teamProgress(team);
            return (
              <li key={team.teamId} className="brain-company-team-card">
                <header className="brain-company-team-header">
                  <div>
                    <h3>{team.teamName}</h3>
                    <p className="brain-company-team-lead">Owner · {team.teamLead}</p>
                  </div>
                  <div className="brain-company-team-stats">
                    <span className="brain-company-team-stat">
                      <strong>{team.filled}</strong> / {team.plannedHires} hired
                    </span>
                    <span className={`brain-company-status-pill brain-company-status-${status}`}>
                      {STATUS_LABEL[status]}
                    </span>
                  </div>
                </header>

                <div className="brain-company-progress">
                  <div
                    className="brain-company-progress-fill"
                    style={{ width: `${Math.round(progress * 100)}%` }}
                  />
                </div>

                <div className="brain-company-quarters">
                  {team.quarters.map((q) => (
                    <div key={q.q} className="brain-company-quarter">
                      <div className="brain-company-quarter-head">
                        <span className="brain-company-quarter-label">{q.q}</span>
                        <span className="brain-company-quarter-count">
                          {q.filled}/{q.planned}
                        </span>
                      </div>
                      {q.openRoles.length > 0 && (
                        <ul className="brain-company-quarter-roles">
                          {q.openRoles.map((role) => (
                            <li key={role}>{role}</li>
                          ))}
                        </ul>
                      )}
                      {q.openRoles.length === 0 && q.planned === q.filled && q.planned > 0 && (
                        <p className="brain-company-quarter-empty">Closed.</p>
                      )}
                      {q.openRoles.length === 0 && q.planned === 0 && (
                        <p className="brain-company-quarter-empty">No hires planned.</p>
                      )}
                    </div>
                  ))}
                </div>

                {team.notes && (
                  <p className="brain-company-team-notes">
                    <AlertTriangle size={11} strokeWidth={1.8} />
                    <span>{team.notes}</span>
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
